import { useState } from 'react';
import { useToast } from '@shared/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { CRMApiAdapter, ExportCRMPayload } from '../adapters/CRMApiAdapter';

export const useExportCRMExcel = () => {
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation('crmModule');

  const exportCRMOrders = async (
    search?: string,
    teamIds?: string,
    statusId?: number,
    shopIds?: string[],
    tagId?: number,
    shippingCountryCodes?: string,
    paymentMethodIds?: string[],
    productIds?: string[],
    dateFrom?: string,
    dateTo?: string,
    sorting?: { field: string; direction: 'asc' | 'desc' },
    visibleColumns?: string[],
  ) => {
    setIsExporting(true);

    try {
      const httpClient = createHttpClient(apiClientWH);
      const crmAdapter = new CRMApiAdapter(httpClient);

      // Construir el payload de exportación
      const payload: ExportCRMPayload = {};

      // Agregar término de búsqueda si existe
      if (search) {
        payload.search = search;
      }
      if (teamIds) {
        payload.team_ids = teamIds;
      }

      // Agregar filtros
      if (statusId) {
        payload.crm_status_id = statusId;
      }
      if (shopIds && shopIds.length > 0) {
        payload.shop_ids = shopIds.join(',');
      }
      if (tagId) {
        payload.tag_id = tagId;
      }
      if (shippingCountryCodes) {
        payload.shipping_country_codes = shippingCountryCodes;
      }
      if (paymentMethodIds && paymentMethodIds.length > 0) {
        payload.payment_method_ids = paymentMethodIds.join(',');
      }
      if (productIds && productIds.length > 0) {
        payload.product_ids = productIds.join(',');
      }
      if (dateFrom) {
        payload.date_from = dateFrom;
      }
      if (dateTo) {
        payload.date_to = dateTo;
      }

      // Agregar ordenamiento
      const sortField = sorting?.field || 'created_at';
      const sortDirection = sorting?.direction || 'desc';
      payload.sort_by = sortField;
      payload.sort_direction = sortDirection as 'asc' | 'desc';

      const response = await crmAdapter.exportCRMOrders(payload, visibleColumns);

      if (response.url_file) {
        // Abrir el archivo Excel en una nueva pestaña
        window.open(response.url_file, '_blank');

        toast({
          title: t('export.success.title', 'Exportación exitosa'),
          description: t('export.success.description', 'El archivo Excel se ha generado correctamente.'),
          variant: 'default',
        });
      } else {
        throw new Error('No se recibió URL del archivo');
      }
    } catch (error: any) {
      toast({
        title: t('export.error.title', 'Error al exportar'),
        description: error.message || t('export.error.unexpected', 'Ocurrió un error inesperado al exportar.'),
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  return {
    exportCRMOrders,
    isExporting,
  };
};
